import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
  GraduationCap,
  Filter,
  Save,
  Loader2,
  Search,
  X,
  Columns3,
  AlertTriangle,
  Plus,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAjusteGrade } from './AjusteGradeContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Select, Button, Modal } from '../../Components/ui';
import { useToast, useConfirm } from '../../Components/Feedback';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { corCardNivel } from '../../utils/niveis';
import { parseLaudos } from '../../Components/AlunoBadges';
import { API_URL } from '../../api/config';

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const HORARIOS = ['HR 1', 'HR 2', 'HR 3'];
const TURNOS = ['Manhã', 'Tarde', 'Noite'];
// Chaves de FiltroColuna que são de horário ("Segunda-HR 1" etc.) — usado pra
// distinguir de uma coluna de dado do inscrito (turno, sexo, saúde...) na
// hora de decidir se um aluno sem nenhuma matrícula pode ser exemptado desse
// filtro específico (ver alunosExibidos).
const HORARIO_CHAVES = new Set(
  DIAS_SEMANA.flatMap((dia) => HORARIOS.map((horario) => `${dia}-${horario}`)),
);

// Tira acento, ignora maiúscula/minúscula e colapsa espaço duplicado — usado
// na busca de aluno pra filtrar em memória (ver resultadosBusca) do mesmo
// jeito que a tabela já se comporta no banco (collation utf8mb4_..._ai_ci =
// "accent insensitive"): buscar "jose" encontra "José" e vice-versa. O espaço
// duplicado é pra alguns nomes cadastrados com espaço a mais entre partes
// (ex.: "David  Samuel  Borges da Silva") — sem colapsar, colar/digitar o
// nome com espaçamento normal nunca batia, mesmo tudo mais certo.
// `MARCAS_DIACRITICAS` é montado por código de caractere (não por escape
// `\uXXXX` direto no literal do regex) de propósito — mais seguro contra
// qualquer ferramenta/editor que normalize o arquivo e "recomponha" um
// acento literal (U+0300–U+036F) escrito cru no meio do código-fonte.
const MARCAS_DIACRITICAS = new RegExp(
  '[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']',
  'g',
);
const normalizarBusca = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(MARCAS_DIACRITICAS, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// Calcula idade em anos completos a partir de data_nascimento (YYYY-MM-DD ou
// ISO) — considera mês/dia pra não arredondar pra cima antes do aniversário.
//
// Extrai ano/mês/dia via regex, NUNCA via `new Date(string).getDate()`: uma
// data "YYYY-MM-DD" é só ano/mês/dia, sem hora nem fuso, mas `new Date(...)`
// interpreta esse texto como meia-noite EM UTC, e getDate()/getMonth()/
// getFullYear() leem de volta em horário LOCAL — pra qualquer fuso negativo
// (Brasil, UTC-3), isso volta pro dia anterior (meia-noite UTC de 01/09 =
// 21h LOCAL de 31/08), deslocando o aniversário e errando a idade perto da
// virada do mês/dia. `hoje`, por outro lado, é o momento atual de verdade,
// então esse (sim) deve mesmo usar hora local — só a data de nascimento
// precisa desse cuidado, por representar um dia puro, sem instante real.
const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const match = String(dataNascimento).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const anoNasc = Number(match[1]),
    mesNasc = Number(match[2]),
    diaNasc = Number(match[3]);

  const hoje = new Date();
  let idade = hoje.getFullYear() - anoNasc;
  const mesAtual = hoje.getMonth() + 1;
  const aindaNaoFezAniversario =
    mesAtual < mesNasc || (mesAtual === mesNasc && hoje.getDate() < diaNasc);
  if (aindaNaoFezAniversario) idade--;
  return idade;
};

// Mesmo cuidado de calcularIdade: extrai por regex em vez de passar por
// `new Date(...)`, pra não sofrer o mesmo deslocamento de fuso-horário numa
// data pura (sem hora).
const formatarDataBR = (data) => {
  if (!data) return '-';
  const match = String(data).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '-';
  return `${match[3]}/${match[2]}/${match[1]}`;
};

// Mesma regra de compatibilidade turno-aluno x turno-turma do backend
// (regras-matricula.js: podeMatricular) e de CadastrarAluno.js (compativel)
// — ensaio (turno "Noite") sempre permitido, o resto tem que bater com o
// turno do aluno. Usada pra decidir, ao trocar o turno direto na grade,
// quais matrículas atuais dele ficariam incompatíveis.
const compativelTurno = (turnoAluno, turnoTurma) => {
  if (!turnoAluno || !turnoTurma) return true;
  if (String(turnoTurma).trim().toLowerCase() === 'noite') return true;
  return String(turnoAluno).trim().toLowerCase() === String(turnoTurma).trim().toLowerCase();
};

// Colunas extras de dados do inscrito, hoje ausentes da grade (que só mostra
// nome + turno/turma/idade). Cada item já sabe como formatar seu próprio
// valor, pra não duplicar essa lógica entre o painel de toggle e a tabela.
// "turno" tem uma exceção: a tabela abaixo (LinhaAluno) renderiza ela como um
// <select> editável em vez de texto — o `formatar` aqui só serve pro filtro
// de coluna, que precisa de um valor de texto pra comparar/agrupar opções.
const COLUNAS_ALUNO = [
  { chave: 'turno', label: 'Turno', formatar: (v) => v || '-' },
  { chave: 'saude', label: 'Saúde', formatar: (v) => v || '-' },
  {
    chave: 'sexo',
    label: 'Sexo',
    formatar: (v) => (v === 'M' ? 'Masculino' : v === 'F' ? 'Feminino' : v || '-'),
  },
  { chave: 'telefone', label: 'Telefone', formatar: (v) => v || '-' },
  { chave: 'transporte', label: 'Transporte', formatar: (v) => v || '-' },
  {
    chave: 'status',
    label: 'Status',
    formatar: (v) => ({ ativo: 'Ativo', espera: 'Em espera', inativo: 'Inativo' })[v] || v || '-',
  },
  { chave: 'escola_atual', label: 'Escola Atual', formatar: (v) => v || '-' },
  { chave: 'data_nascimento', label: 'Data de Nasc.', formatar: (v) => formatarDataBR(v) },
  {
    chave: 'idade',
    label: 'Idade',
    formatarAluno: (aluno) => {
      const idade = calcularIdade(aluno.data_nascimento);
      return idade == null ? '-' : `${idade} anos`;
    },
  },
  {
    chave: 'nivel',
    label: 'Nível',
    formatarAluno: (aluno) =>
      aluno.nivel == null
        ? '-'
        : aluno.subnivel
          ? `${aluno.nivel}.${aluno.subnivel}`
          : String(aluno.nivel),
  },
  { chave: 'ponto', label: 'Ponto', formatar: (v) => v || '-' },
  { chave: 'acompanhamento', label: 'Acompanhamento', formatar: (v) => v || '-' },
  { chave: 'informacoes_gerais', label: 'Informações Gerais', formatar: (v) => v || '-' },
  { chave: 'Inf', label: 'Informações Adicionais', formatar: (v) => v || '-' },
  {
    chave: 'bolsista',
    label: 'Bolsista',
    formatar: (v) => (v === 1 || v === true || v === '1' ? 'Bolsista' : 'Não bolsista'),
  },
  {
    chave: 'lider_servidor',
    label: 'Líder Servidor',
    formatar: (v) => (v === 1 || v === true || v === '1' ? 'Líder Servidor' : 'Não'),
  },
  {
    chave: 'laudos',
    label: 'Laudos Médicos',
    formatarAluno: (aluno) => {
      const lista = parseLaudos(aluno.laudos);
      return lista.length > 0 ? lista.join(', ') : '-';
    },
  },
  { chave: 'cuidados_medicos', label: 'Cuidados Especiais', formatar: (v) => v || '-' },
  { chave: 'turma', label: 'Turma / Série', formatar: (v) => v || '-' },
  { chave: 'ponto_desembarque', label: 'Ponto Desembarque', formatar: (v) => v || '-' },
  {
    chave: 'data_cadastro',
    label: 'Data Cadastro',
    formatarAluno: (aluno) =>
      formatarDataBR(aluno.data_cadastro || (aluno.criado_em ? String(aluno.criado_em).slice(0, 10) : '')) || '-',
  },
  { chave: 'motivo_desistencia', label: 'Motivo Desistência', formatar: (v) => v || '-' },
];

// Uma coluna "Educador — <Área>" por área (mesma lista de AREAS usada nos
// filtros de área da grade). Só a definição (chave/label/área) é estática —
// puramente derivada de AREAS, sem depender de dado nenhum carregado — porque
// o VALOR de cada uma (quem são os educadores do aluno naquela área) depende
// de matrículas/atividades, que só existem dentro do componente; ver
// `formatarColunaAluno`/`obterEducadorArea` mais abaixo, que sabem lidar com
// `coluna.area` separado de `coluna.formatar`/`coluna.formatarAluno`. Uma
// área pode ter mais de um educador pro mesmo aluno (ex.: Cultural, se ele
// frequenta mais de uma turma dessa área) — Educacional, no fluxo atual, só
// tem uma turma por aluno, então sempre aparece um nome só.
const COLUNAS_EDUCADOR_POR_AREA = AREAS.map((area) => ({
  chave: `educador_${area.value}`,
  label: `Educador — ${area.label}`,
  area: area.value,
}));

const TODAS_COLUNAS_ALUNO = [...COLUNAS_ALUNO, ...COLUNAS_EDUCADOR_POR_AREA];

const CHAVE_LOCALSTORAGE_COLUNAS = 'ajusteGrade:colunasAluno';
const CHAVE_LOCALSTORAGE_DIAS = 'ajusteGrade:diasVisiveis';

const lerPreferencia = (chave, valorPadrao) => {
  try {
    const salvo = localStorage.getItem(chave);
    return salvo ? { ...valorPadrao, ...JSON.parse(salvo) } : valorPadrao;
  } catch {
    return valorPadrao;
  }
};

// Filtro por coluna com múltiplas opções (tipo Excel), usado tanto nas colunas
// de horário quanto nas colunas extras de dados do inscrito. Renderiza o
// painel de checkboxes via portal direto em document.body — colocá-lo dentro
// do <th> (sticky, dentro de um Card com overflow-auto) faria o painel ser
// cortado pelo scroll da grade ou disputar z-index com as próprias células
// sticky (mesmo problema já visto no painel "Colunas" antes de virar z-50, só
// que aqui, sendo dentro do container que rola, nem bastaria subir o
// z-index). Como o botão só existe dentro do <th> sticky, rolar a grade
// horizontalmente move o botão de verdade na tela, então o painel aberto
// (posicionado por coordenadas fixas) ficaria com a posição errada — por
// isso precisa fechar ao rolar. `filtrosAbertosRef` é um Set compartilhado
// (ver AjusteGrade) onde este componente se registra SÓ enquanto está aberto;
// o scroll da grade chama essas funções direto (fora do ciclo do React,
// via ref), em vez de depender de um valor de scroll que mudasse a cada
// pixel como prop — isso evitaria re-renderizar todo FiltroColuna da tabela
// (podem ser 20+) a cada evento de scroll, o que é a causa da lentidão que
// esse desenho já teve antes.
// React.memo: sem isso, toda edição de célula (que muda `alteracoes` no
// componente pai) reexecutava os ~20 FiltroColuna do cabeçalho de novo,
// mesmo que `opcoes`/`selecionados` de nenhum deles tivesse mudado.
const FiltroColuna = React.memo(function FiltroColuna({
  chave,
  opcoes,
  selecionados,
  onChange,
  filtrosAbertosRef,
  areaPorOpcao,
}) {
  const [aberto, setAberto] = useState(false);
  const [posicao, setPosicao] = useState(null);
  // Rascunho local (tipo Excel): marcar/desmarcar caixas só muda esse estado
  // daqui — o filtro de verdade (selecionados, que afeta a grade) só recebe
  // a mudança quando o usuário clica "Aplicar". Fechar sem aplicar (Cancelar,
  // clicar fora, Esc, rolar a grade) descarta o rascunho.
  const [rascunho, setRascunho] = useState(selecionados);
  const botaoRef = useRef(null);
  const painelRef = useRef(null);

  useEffect(() => {
    if (!aberto || !filtrosAbertosRef) return;
    const conjunto = filtrosAbertosRef.current;
    const fechar = () => setAberto(false);
    conjunto.add(fechar);
    return () => conjunto.delete(fechar);
  }, [aberto, filtrosAbertosRef]);

  useEffect(() => {
    if (!aberto) return;
    const handleClickFora = (e) => {
      if (
        botaoRef.current &&
        !botaoRef.current.contains(e.target) &&
        painelRef.current &&
        !painelRef.current.contains(e.target)
      ) {
        setAberto(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', handleClickFora);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickFora);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [aberto]);

  const abrir = () => {
    const rect = botaoRef.current.getBoundingClientRect();
    setPosicao({ top: rect.bottom + 4, left: rect.left });
    setRascunho(selecionados);
    setAberto(true);
  };

  const aplicar = () => {
    onChange(chave, rascunho);
    setAberto(false);
  };

  const alternarValor = (valor) => {
    setRascunho((prev) =>
      prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor],
    );
  };

  // Áreas realmente presentes entre as opções desta coluna (ex.: só as turmas
  // de "Segunda HR 2" que têm área) — cada área agrupa um ou mais nomes de
  // turma; marcar a área marca de uma vez todos os nomes daquele grupo (some
  // ao invés de substituir a seleção por nome, que continua funcionando
  // separadamente, célula a célula).
  const opcoesPorArea = useMemo(() => {
    if (!areaPorOpcao) return [];
    const mapa = new Map();
    opcoes.forEach((opcao) => {
      const area = areaPorOpcao.get ? areaPorOpcao.get(opcao) : areaPorOpcao[opcao];
      if (!area) return;
      if (!mapa.has(area)) mapa.set(area, []);
      mapa.get(area).push(opcao);
    });
    return AREAS.filter((a) => mapa.has(a.value)).map((a) => ({
      value: a.value,
      label: a.label,
      opcoes: mapa.get(a.value),
    }));
  }, [areaPorOpcao, opcoes]);

  const alternarArea = (opcoesDaArea) => {
    setRascunho((prev) => {
      const todasSelecionadas = opcoesDaArea.every((op) => prev.includes(op));
      return todasSelecionadas
        ? prev.filter((v) => !opcoesDaArea.includes(v))
        : [...new Set([...prev, ...opcoesDaArea])];
    });
  };

  const ativo = selecionados.length > 0;

  return (
    <>
      <button
        type="button"
        ref={botaoRef}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        title={ativo ? `${selecionados.length} de ${opcoes.length} selecionado(s)` : 'Filtrar'}
        className={`w-full text-[9px] normal-case font-normal rounded border px-1 py-0.5 text-left truncate focus:outline-none focus:ring-1 focus:ring-blue-500/30 ${
          ativo
            ? 'border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400'
        }`}
      >
        {ativo ? `${selecionados.length} selec.` : '(Todas)'}
      </button>
      {aberto &&
        posicao &&
        createPortal(
          <div
            ref={painelRef}
            style={{ position: 'fixed', top: posicao.top, left: posicao.left }}
            className="z-50 w-48 max-h-80 flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
          >
            <div className="flex items-center justify-between px-2 pt-2 pb-1 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
              <button
                type="button"
                onClick={() => setRascunho(opcoes)}
                className="text-[10px] font-normal normal-case text-blue-600 dark:text-blue-400 hover:underline"
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => setRascunho([])}
                className="text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500 hover:underline"
              >
                Nenhuma
              </button>
            </div>
            <div className="px-2 py-2 flex-1 overflow-y-auto">
              {opcoesPorArea.length > 0 && (
                <>
                  <div className="text-[9px] font-medium normal-case text-gray-400 dark:text-gray-500 mb-1">
                    Área
                  </div>
                  <div className="space-y-1 pb-2 mb-2 border-b border-gray-100 dark:border-gray-800">
                    {opcoesPorArea.map(({ value, label, opcoes: opcoesDaArea }) => (
                      <label
                        key={value}
                        className="flex items-center gap-1.5 text-[10px] font-normal normal-case text-gray-700 dark:text-gray-300 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={opcoesDaArea.every((op) => rascunho.includes(op))}
                          onChange={() => alternarArea(opcoesDaArea)}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500/30"
                        />
                        <span className="truncate">{label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <div className="space-y-1">
                {opcoes.map((valor) => (
                  <label
                    key={valor}
                    className="flex items-center gap-1.5 text-[10px] font-normal normal-case text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={rascunho.includes(valor)}
                      onChange={() => alternarValor(valor)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500/30"
                    />
                    <span className="truncate">{valor}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-2 py-2 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
              <button
                type="button"
                onClick={() => setAberto(false)}
                className="text-[10px] font-normal normal-case text-gray-500 dark:text-gray-400 hover:underline"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={aplicar}
                className="text-[10px] font-medium normal-case text-white bg-blue-600 hover:bg-blue-700 rounded px-2 py-1 transition-colors"
              >
                Aplicar
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

// Uma linha da grade (um aluno × todas as colunas). React.memo + a fatia
// `alteracoesDoAluno` com referência estável (ver alteracoesPorAluno em
// AjusteGrade) é o que faz uma seleção numa célula reexecutar só ESTA linha
// — antes, qualquer edição em qualquer célula da tela reconstruía as
// centenas de linhas da grade inteira, mesmo as que não tinham nada a ver
// com a mudança.
const LinhaAluno = React.memo(function LinhaAluno({
  aluno,
  idx,
  colunasAlunoAtivas,
  formatarColunaAluno,
  diasExibidos,
  matriculaSlots,
  alteracoesDoAluno,
  turno,
  atividadesAtivas,
  areaPorAtividadeId,
  areasAtivas,
  abrirCelula,
  onMudarTurno,
}) {
  // "MANHÃ"/"TARDE" (cadastro) vs "Manhã"/"Tarde" (valor das opções) — sem
  // normalizar, o <select> não reconhece o valor atual e mostra vazio.
  const turnoSelectValue =
    ['Manhã', 'Tarde'].find(
      (t) =>
        t.toLowerCase() ===
        String(aluno.turno || '')
          .trim()
          .toLowerCase(),
    ) || '';
  return (
    <tr
      className={idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/40 dark:bg-gray-800/30'}
    >
      <td
        className={`px-3 py-1.5 sticky left-0 border border-gray-200 dark:border-gray-700 shadow-[inset_-2px_0_0_0_#e5e7eb] dark:shadow-[inset_-2px_0_0_0_#374151] z-10 ${corCardNivel(aluno.nivel) || (idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800')}`}
      >
        <p className="font-medium text-gray-900 dark:text-gray-100 truncate" translate="no">
          {aluno.nome}
        </p>
        <p className="text-[10px] font-normal text-gray-400 dark:text-gray-500 truncate">
          {[
            aluno.turno,
            aluno.turma,
            calcularIdade(aluno.data_nascimento) != null
              ? `${calcularIdade(aluno.data_nascimento)} anos`
              : null,
          ]
            .filter(Boolean)
            .join(' · ') || ' '}
        </p>
      </td>
      {colunasAlunoAtivas.map((coluna) => {
        if (coluna.chave === 'turno') {
          return (
            <td
              key={coluna.chave}
              className="px-1.5 py-1 border border-gray-200 dark:border-gray-700"
            >
              <select
                value={turnoSelectValue}
                onChange={(e) => onMudarTurno(aluno, e.target.value)}
                className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">-</option>
                <option value="Manhã">Manhã</option>
                <option value="Tarde">Tarde</option>
              </select>
            </td>
          );
        }
        const valorFormatado = formatarColunaAluno(coluna, aluno);
        if (coluna.chave === 'lider_servidor') {
          return (
            <td
              key={coluna.chave}
              className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 text-center"
              title={valorFormatado}
            >
              {aluno.lider_servidor ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/80">
                  ⭐ Líder
                </span>
              ) : (
                <span className="text-gray-400 text-xs">-</span>
              )}
            </td>
          );
        }
        if (coluna.chave === 'bolsista') {
          return (
            <td
              key={coluna.chave}
              className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 text-center"
              title={valorFormatado}
            >
              {aluno.bolsista ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/80">
                  Bolsista
                </span>
              ) : (
                <span className="text-gray-400 text-xs">-</span>
              )}
            </td>
          );
        }
        if (coluna.chave === 'laudos') {
          const listaLaudos = parseLaudos(aluno.laudos);
          return (
            <td
              key={coluna.chave}
              className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 max-w-[180px] truncate"
              title={valorFormatado}
            >
              {listaLaudos.length > 0 ? (
                <div className="flex items-center gap-1 flex-wrap">
                  {listaLaudos.map((l) => (
                    <span
                      key={l}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60"
                    >
                      {l}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400 text-xs">-</span>
              )}
            </td>
          );
        }
        return (
          <td
            key={coluna.chave}
            className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 max-w-[160px] truncate"
            title={valorFormatado}
          >
            {valorFormatado}
          </td>
        );
      })}
      {diasExibidos.map((dia) =>
        HORARIOS.map((horario) => {
          const slot = `${dia}-${horario}`;
          const matricula = matriculaSlots?.[slot];
          const atividadeId = alteracoesDoAluno?.[`${aluno.id}-${slot}`];
          const alterado = atividadeId !== undefined;
          // Mesma lógica de obterLabelSlot (AjusteGrade), mas sem depender do
          // objeto `alteracoes` inteiro — só da fatia já isolada deste aluno,
          // que é o que mantém essa linha memoizável de verdade.
          const labelAtual = alterado
            ? !atividadeId
              ? '-'
              : atividadesAtivas.find((a) => String(a.id) === String(atividadeId))?.nome || '-'
            : matricula?.nome_atividade || '-';

          // Turma da célula pertence a uma área desligada nos botões de cima?
          // Se sim, apaga visualmente (mas continua editável — é destaque
          // visual pra revisão, não travamento).
          const idAtividadeAtual = alterado ? atividadeId : matricula?.id_atividade;
          const areaAtual = idAtividadeAtual
            ? areaPorAtividadeId.get(String(idAtividadeAtual))
            : null;
          const areaApagada = !alterado && areaAtual && areasAtivas[areaAtual] === false;

          return (
            <td
              key={slot}
              className="px-1 py-1 border border-gray-200 dark:border-gray-700 align-top"
            >
              <button
                type="button"
                onClick={() => abrirCelula(aluno.id, dia, horario, turno)}
                title={areaApagada ? `${labelAtual} (área oculta)` : labelAtual}
                className={`w-full h-7 px-1.5 rounded-md border text-[10px] text-left truncate focus:outline-none focus:ring-1 focus:ring-blue-500/30 transition-all ${
                  alterado
                    ? 'border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300'
                    : areaApagada
                      ? 'border-dashed border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 text-gray-300 dark:text-gray-600 opacity-50'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span translate="no">{labelAtual}</span>
              </button>
              <div
                className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 truncate leading-tight"
                translate="no"
              >
                {areaApagada ? ' ' : matricula?.nome_professor || ' '}
              </div>
            </td>
          );
        }),
      )}
    </tr>
  );
});

// Tela de edição em lote da grade: uma matriz aluno × (dia, horário) onde cada
// célula é um <select> de turma (atividade). Mudanças ficam pendentes em
// `alteracoes` (não são salvas por célula) até o usuário clicar em "Salvar
// Alterações", que envia tudo de uma vez pra POST /api/matriculas.
//
// As opções de cada célula só mostram turmas que batem com aquele dia+horário+
// turno exatos (ver atividadesFiltradas) — desde a migração que separou
// `atividades` por horário (migrate-split-atividades-por-horario.js), isso
// finalmente filtra de verdade; turmas encerradas ficam de fora.
function AjusteGrade() {
  const { institutionId } = useInstitution();
  const token = localStorage.getItem('authToken');
  const { podeEditar, podeExportar } = usePermissao('/ajuste-grade');
  const queryClient = useQueryClient();

  // Filtros de cima, filtro por coluna (tipo Excel: chave "dia-horario" ou
  // coluna.chave -> array de valores aceitos nessa coluna) e filtro por área
  // vivem no mesmo Context de AlunosSelecionados/alteracoes (ver
  // AjusteGradeContext.js) — de propósito, pra sobreviver quando o usuário
  // troca de menu e volta pra essa tela.
  const {
    alunosSelecionados,
    setAlunosSelecionados,
    alteracoes,
    setAlteracoes,
    atividadeFiltro,
    setAtividadeFiltro,
    professorFiltro,
    setProfessorFiltro,
    diaFiltro,
    setDiaFiltro,
    horarioFiltro,
    setHorarioFiltro,
    turnoFiltro,
    setTurnoFiltro,
    filtrosColuna,
    setFiltrosColuna,
    areasAtivas,
    setAreasAtivas,
    buscaAluno,
    setBuscaAluno,
    limparFiltros,
  } = useAjusteGrade();
  const alternarArea = (value) => setAreasAtivas((prev) => ({ ...prev, [value]: !prev[value] }));
  const [salvando, setSalvando] = useState(false);

  // Colunas extras de dados do inscrito e dias da semana visíveis na grade —
  // preferência salva em localStorage pra sobreviver a um F5 (a tela mostra
  // muita coisa de propósito, então cada usuário deixa só o que usa).
  const [colunasAlunoVisiveis, setColunasAlunoVisiveis] = useState(() =>
    lerPreferencia(
      CHAVE_LOCALSTORAGE_COLUNAS,
      Object.fromEntries(TODAS_COLUNAS_ALUNO.map((c) => [c.chave, false])),
    ),
  );
  const [diasVisiveis, setDiasVisiveis] = useState(() =>
    lerPreferencia(CHAVE_LOCALSTORAGE_DIAS, Object.fromEntries(DIAS_SEMANA.map((d) => [d, true]))),
  );
  const [painelColunasAberto, setPainelColunasAberto] = useState(false);
  const painelColunasRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(CHAVE_LOCALSTORAGE_COLUNAS, JSON.stringify(colunasAlunoVisiveis));
  }, [colunasAlunoVisiveis]);
  useEffect(() => {
    localStorage.setItem(CHAVE_LOCALSTORAGE_DIAS, JSON.stringify(diasVisiveis));
  }, [diasVisiveis]);

  // Fecha o painel de colunas ao clicar fora dele (mesmo cuidado de UX que os
  // outros dropdowns/modais da tela já têm) ou ao apertar Esc.
  useEffect(() => {
    if (!painelColunasAberto) return;
    const handleClickFora = (e) => {
      if (painelColunasRef.current && !painelColunasRef.current.contains(e.target)) {
        setPainelColunasAberto(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setPainelColunasAberto(false);
    };
    document.addEventListener('mousedown', handleClickFora);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickFora);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [painelColunasAberto]);

  const alternarColunaAluno = (chave) =>
    setColunasAlunoVisiveis((prev) => ({ ...prev, [chave]: !prev[chave] }));
  const alternarDiaVisivel = (dia) => setDiasVisiveis((prev) => ({ ...prev, [dia]: !prev[dia] }));

  const colunasAlunoAtivas = useMemo(
    () => TODAS_COLUNAS_ALUNO.filter((c) => colunasAlunoVisiveis[c.chave]),
    [colunasAlunoVisiveis],
  );
  const diasExibidos = useMemo(
    () => DIAS_SEMANA.filter((d) => diasVisiveis[d] !== false),
    [diasVisiveis],
  );
  const diasOcultos = DIAS_SEMANA.length - diasExibidos.length;

  // Indicador de posição da rolagem horizontal da grade (barra fininha logo
  // acima da tabela, sempre visível mesmo antes de rolar pra baixo até a
  // scrollbar nativa) — dá pra ver de cara quanto já rolou e quanto falta.
  //
  // De propósito NÃO fica em state do React: essa tabela pode ter centenas de
  // células, e o evento nativo `scroll` dispara muitas vezes por segundo — se
  // cada disparo passasse por setState (como era antes), a tabela inteira
  // re-renderizaria a cada pixel rolado, e é exatamente isso que causava a
  // lentidão perceptível no scroll horizontal. Em vez disso, o handler edita
  // o estilo da barra direto no DOM via ref, fora do ciclo do React.
  const gradeScrollRef = useRef(null);
  const scrollBarRef = useRef(null);
  // Filtros de coluna abertos no momento (ver FiltroColuna) — cada um se
  // registra aqui só enquanto está aberto; rolar a grade fecha só os que
  // realmente estão abertos, sem precisar re-renderizar as outras colunas.
  const filtrosAbertosRef = useRef(new Set());
  // Só recalcula a largura/posição da barra — usada tanto pelo scroll de
  // verdade quanto pelo efeito abaixo que reajusta a barra quando o
  // conjunto de linhas muda por outro motivo (busca, filtro de topo etc.),
  // sem fechar nenhum filtro de coluna aberto nesses casos.
  const recalcularBarraScroll = useCallback(() => {
    const el = gradeScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const posicaoBruta = maxScroll > 0 ? (el.scrollLeft / maxScroll) * 100 : 0;
    const largura = Math.min(100, (el.clientWidth / el.scrollWidth) * 100);
    const posicao = posicaoBruta * (1 - largura / 100);
    if (scrollBarRef.current) {
      scrollBarRef.current.style.width = `${largura}%`;
      scrollBarRef.current.style.marginLeft = `${posicao}%`;
    }
  }, []);
  // Só isso (rolagem de verdade) fecha os filtros de coluna abertos — antes
  // essa limpeza estava dentro da mesma função usada pra reajustar a barra
  // em QUALQUER mudança no conjunto de linhas, então marcar uma opção no
  // filtro (que muda alunosExibidos) fechava o próprio filtro que acabou de
  // ser usado.
  const handleScrollGrade = useCallback(() => {
    recalcularBarraScroll();
    if (filtrosAbertosRef.current.size > 0) {
      filtrosAbertosRef.current.forEach((fechar) => fechar());
      filtrosAbertosRef.current.clear();
    }
  }, [recalcularBarraScroll]);

  // Transfere imediatamente o giro vertical excedente da roda/trackball para o
  // container da página (<div class="flex-1 overflow-y-auto"> do Layout) assim
  // que a tabela chega no limite superior ou inferior, evitando o "scroll
  // latching" do navegador quando o cursor permanece parado sobre a tabela.
  const handleWheelGrade = useCallback((e) => {
    const el = gradeScrollRef.current;
    if (!el || !e.deltaY || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

    const fator = e.deltaMode === 1 ? 24 : e.deltaMode === 2 ? el.clientHeight : 1;
    const deltaY = e.deltaY * fator;
    const maxScrollTop = el.scrollHeight - el.clientHeight;

    const encontrarScrollParent = (node) => {
      let atual = node?.parentElement;
      while (atual && atual !== document.body && atual !== document.documentElement) {
        const style = window.getComputedStyle(atual);
        if (/(auto|scroll)/.test(style.overflowY) && atual.scrollHeight > atual.clientHeight) {
          return atual;
        }
        atual = atual.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

    if (deltaY > 0) {
      const restante = maxScrollTop - el.scrollTop;
      if (restante <= 1) {
        const pai = encontrarScrollParent(el);
        if (pai) pai.scrollTop += deltaY;
      } else if (deltaY > restante) {
        const pai = encontrarScrollParent(el);
        if (pai) pai.scrollTop += deltaY - restante;
      }
    } else if (deltaY < 0) {
      if (el.scrollTop <= 1) {
        const pai = encontrarScrollParent(el);
        if (pai) pai.scrollTop += deltaY;
      } else if (Math.abs(deltaY) > el.scrollTop) {
        const pai = encontrarScrollParent(el);
        if (pai) pai.scrollTop += deltaY + el.scrollTop;
      }
    }
  }, []);

  // Clicar ou arrastar na barra indicadora também rola a grade — igual à
  // scrollbar nativa de baixo, mas essa barra fica sempre visível acima da
  // tabela (a nativa só aparece embaixo do que está visível na tela). Move o
  // `scrollLeft` de verdade; a própria barra se atualiza sozinha por causa do
  // evento `scroll` nativo que isso dispara (mesmo handler de sempre, sem
  // duplicar lógica de posição aqui).
  const handleBarraMouseDown = useCallback((e) => {
    const el = gradeScrollRef.current;
    const trackEl = e.currentTarget;
    if (!el) return;
    const trackRect = trackEl.getBoundingClientRect();
    const moverPara = (clientX) => {
      const fracao = Math.min(1, Math.max(0, (clientX - trackRect.left) / trackRect.width));
      el.scrollLeft = fracao * (el.scrollWidth - el.clientWidth);
    };
    moverPara(e.clientX);
    const handleMouseMove = (ev) => moverPara(ev.clientX);
    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  const toast = useToast();
  const confirm = useConfirm();

  // Estado para busca de alunos (buscaAluno vem do contexto — persiste com
  // os outros filtros)
  const [buscaAlunoDebounced, setBuscaAlunoDebounced] = useState('');

  // Debounce busca
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaAlunoDebounced(buscaAluno);
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaAluno]);

  const { data: atividades = [], isSuccess: atividadesCarregadas } = useQuery({
    queryKey: ['atividades', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/atividades`, {
        headers: {
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Erro ao carregar atividades');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!institutionId && !!token,
    // Essas turmas podem ter sido alteradas em outra tela (Grade, Grade por
    // Turma) que usa sua própria chave de cache — refetchOnMount 'always'
    // ignora o staleTime padrão (5min, ver App.js) e busca de novo toda vez
    // que essa tela é aberta, sem bloquear a exibição do que já estava em cache.
    refetchOnMount: 'always',
    // Cobre o caso da tela já estar aberta numa aba em segundo plano enquanto
    // a alteração é feita em outra — refetchOnWindowFocus 'always' busca de
    // novo ao voltar o foco pra essa aba, sem esperar o staleTime.
    refetchOnWindowFocus: 'always',
  });

  // Nomes de professor pro datalist do form "+ Nova turma" (painel de
  // atribuir turma na célula, ver criarTurmaCelula) — mesmo endpoint/padrão
  // já usado em Turmas.js.
  const { data: professoresExistentes = [] } = useQuery({
    queryKey: ['professores-nomes', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/professores`, {
        headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar educadores');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!institutionId && !!token,
  });

  // Turmas encerradas não entram como opção pra matricular ninguém — só
  // aparecem em Turmas.js. Área desligada (botões abaixo) também tira a
  // turma daqui (mesma regra de GradeTurmas.js, mesmo comportamento).
  const atividadesAtivas = useMemo(
    () =>
      atividades.filter((atv) => !atv.data_fim && (!atv.area || areasAtivas[atv.area] !== false)),
    [atividades, areasAtivas],
  );

  // Área de cada atividade por id (a partir da lista SEM filtro de área — a
  // dimensão do filtro é decidida célula a célula na hora de desenhar a
  // grade, não aqui). Usado pra saber, em cada célula, se a turma atual
  // pertence a uma área desligada e precisa aparecer apagada.
  const areaPorAtividadeId = useMemo(
    () => new Map(atividades.map((a) => [String(a.id), a.area])),
    [atividades],
  );

  const atividadePorId = useMemo(
    () => new Map(atividades.map((a) => [String(a.id), a])),
    [atividades],
  );

  const temProfessor = useCallback(
    (atv, nomeProf) =>
      Boolean(
        atv &&
          (atv.nome_professor === nomeProf ||
            (Array.isArray(atv.professores_adicionais) &&
              atv.professores_adicionais.some((p) => p.nome === nomeProf))),
      ),
    [],
  );

  // Filtros cruzados: as opções de CADA dropdown levam em conta o que já foi
  // escolhido nos OUTROS 4 (mas nunca nele mesmo, senão escolher algo faria a
  // própria opção escolhida sumir da lista). A fonte de verdade são as turmas
  // reais (atividadesAtivas), não as matrículas — é o dado mais direto pra
  // saber quais combinações de atividade+professor+dia+horário+turno existem.
  const correspondeAosFiltros = (atv, ignorar) => {
    if (
      ignorar !== 'atividade' &&
      atividadeFiltro &&
      atividadeFiltro !== '__sem_matricula__' &&
      String(atv.id) !== String(atividadeFiltro)
    )
      return false;
    if (ignorar !== 'professor' && professorFiltro && !temProfessor(atv, professorFiltro))
      return false;
    if (ignorar !== 'dia' && diaFiltro && atv.dia_semana !== diaFiltro) return false;
    if (ignorar !== 'horario' && horarioFiltro && atv.horario !== horarioFiltro) return false;
    if (ignorar !== 'turno' && turnoFiltro && atv.turno !== turnoFiltro) return false;
    return true;
  };

  // Cada id de atividade já é uma turma específica (nome+dia+horário+turno,
  // ver migrate-split-atividades-por-horario.js) — várias turmas costumam
  // compartilhar o mesmo nome (mesma disciplina, horários diferentes), então
  // o rótulo do dropdown sempre inclui dia/horário/turno, senão pareceriam
  // opções repetidas.
  const opcoesAtividade = useMemo(() => {
    return atividadesAtivas
      .filter((atv) => correspondeAosFiltros(atv, 'atividade'))
      .map((atv) => ({
        value: atv.id,
        label: atv.dia_semana
          ? `${atv.nome} — ${atv.dia_semana} ${atv.horario} (${atv.turno})`
          : atv.nome,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [atividadesAtivas, professorFiltro, diaFiltro, horarioFiltro, turnoFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesProfessor = useMemo(() => {
    const nomes = new Set();
    atividadesAtivas
      .filter((atv) => correspondeAosFiltros(atv, 'professor'))
      .forEach((atv) => {
        if (atv.nome_professor) nomes.add(atv.nome_professor);
        if (Array.isArray(atv.professores_adicionais)) {
          atv.professores_adicionais.forEach((p) => {
            if (p.nome) nomes.add(p.nome);
          });
        }
      });
    return Array.from(nomes).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [atividadesAtivas, atividadeFiltro, diaFiltro, horarioFiltro, turnoFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesDia = useMemo(() => {
    const presentes = new Set(
      atividadesAtivas
        .filter((atv) => correspondeAosFiltros(atv, 'dia'))
        .map((atv) => atv.dia_semana),
    );
    return DIAS_SEMANA.filter((d) => presentes.has(d));
  }, [atividadesAtivas, atividadeFiltro, professorFiltro, horarioFiltro, turnoFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesHorario = useMemo(() => {
    const presentes = new Set(
      atividadesAtivas
        .filter((atv) => correspondeAosFiltros(atv, 'horario'))
        .map((atv) => atv.horario),
    );
    return HORARIOS.filter((h) => presentes.has(h));
  }, [atividadesAtivas, atividadeFiltro, professorFiltro, diaFiltro, turnoFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesTurno = useMemo(() => {
    const presentes = new Set(
      atividadesAtivas.filter((atv) => correspondeAosFiltros(atv, 'turno')).map((atv) => atv.turno),
    );
    return ['Manhã', 'Tarde', 'Noite'].filter((t) => presentes.has(t));
  }, [atividadesAtivas, atividadeFiltro, professorFiltro, diaFiltro, horarioFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  // Se a escolha atual de um filtro deixou de existir nas opções (porque outro
  // filtro mudou e estreitou o leque), limpa ele — nunca deixa um dropdown com
  // um valor selecionado que já não aparece na sua própria lista de opções.
  // `atividadesCarregadas` (isSuccess da query) é essencial aqui: sem essa
  // guarda, um filtro restaurado do localStorage (ver AjusteGradeContext) era
  // limpo por engano nesse efeito ANTES da primeira resposta da API chegar —
  // opcoesDia/opcoesHorario/etc. ficam vazias enquanto `atividades` ainda é
  // [] (valor padrão do useQuery), então "Segunda" parecia uma opção
  // inválida só por ainda não ter carregado, não por ser inválida de verdade.
  React.useEffect(() => {
    if (!atividadesCarregadas) return;
    if (
      atividadeFiltro &&
      atividadeFiltro !== '__sem_matricula__' &&
      !opcoesAtividade.some((o) => String(o.value) === String(atividadeFiltro))
    )
      setAtividadeFiltro('');
  }, [opcoesAtividade, atividadesCarregadas]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!atividadesCarregadas) return;
    if (professorFiltro && !opcoesProfessor.includes(professorFiltro)) setProfessorFiltro('');
  }, [opcoesProfessor, atividadesCarregadas]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!atividadesCarregadas) return;
    if (diaFiltro && !opcoesDia.includes(diaFiltro)) setDiaFiltro('');
  }, [opcoesDia, atividadesCarregadas]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!atividadesCarregadas) return;
    if (horarioFiltro && !opcoesHorario.includes(horarioFiltro)) setHorarioFiltro('');
  }, [opcoesHorario, atividadesCarregadas]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!atividadesCarregadas) return;
    if (turnoFiltro && !opcoesTurno.includes(turnoFiltro)) setTurnoFiltro('');
  }, [opcoesTurno, atividadesCarregadas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch alunos (base)
  const { data: alunos = [], isLoading: loadingAlunos } = useQuery({
    queryKey: ['alunos', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/alunos`, {
        headers: {
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Erro ao carregar inscritos');
      const raw = await res.json();
      return raw?.data ?? (Array.isArray(raw) ? raw : []);
    },
    enabled: !!institutionId && !!token,
  });

  // Fetch matriculas (base)
  const { data: matriculas = [], isLoading: loadingMatriculas } = useQuery({
    queryKey: ['matriculas', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/matriculas/por-instituicao?status=matriculado`, {
        headers: {
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Erro ao carregar matrículas');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!institutionId && !!token,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  // Alunos com mais de uma matrícula ATIVA no mesmo dia+horário — nunca devia
  // acontecer (ver comentário em backend/matriculas.js), mas um bug antigo já
  // deixou isso acontecer algumas vezes. Carregado sempre (é uma query leve)
  // só pra mostrar a contagem no botão sem precisar abrir o modal primeiro.
  const { data: duplicidades = [] } = useQuery({
    queryKey: ['matriculas-duplicidades', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/matriculas/duplicidades`, {
        headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar duplicidades');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!institutionId && !!token,
  });
  const [duplicidadesModalAberto, setDuplicidadesModalAberto] = useState(false);
  const [resolvendoDuplicidade, setResolvendoDuplicidade] = useState(null); // idmatricula em processamento

  const resolverDuplicidade = async (manter) => {
    setResolvendoDuplicidade(manter);
    try {
      const res = await fetch(`${API_URL}/api/matriculas/duplicidades/resolver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ manter }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao resolver duplicidade');
      toast.success('Duplicidade resolvida.');
      await queryClient.invalidateQueries({ queryKey: ['matriculas-duplicidades'] });
      await queryClient.invalidateQueries({ queryKey: ['matriculas'] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResolvendoDuplicidade(null);
    }
  };

  // Todos os alunos (qualquer status) pra busca "adicionar à visualização" —
  // buscado UMA VEZ ao montar a tela, não a cada tecla digitada. Antes essa
  // busca disparava um GET /api/alunos?nome=X por tecla (com debounce), um
  // round-trip de rede a cada busca; em internet ruim isso é a lentidão
  // sentida a cada ação. `?status=todos` porque o endpoint, quando recebe
  // `nome`, ignora o filtro de status — essa busca hoje também acha aluno
  // inativo/em espera (pra dar pra reativar/revisar), e a troca não pode
  // perder essa capacidade.
  const { data: alunosParaBusca = [] } = useQuery({
    queryKey: ['alunos-para-busca', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/alunos?status=todos`, {
        headers: {
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Erro ao buscar inscritos');
      const raw = await res.json();
      return raw?.data ?? (Array.isArray(raw) ? raw : []);
    },
    enabled: !!institutionId && !!token,
  });

  // Filtra em memória (instantâneo, sem rede) — mesmo resultado de antes,
  // só que sem esperar um round-trip por tecla.
  const resultadosBusca = useMemo(() => {
    const termo = normalizarBusca(buscaAlunoDebounced.trim());
    if (!termo) return [];
    const idsSelecionados = new Set(alunosSelecionados.map((s) => s.id));
    return alunosParaBusca.filter(
      (a) => !idsSelecionados.has(a.id) && normalizarBusca(a.nome).includes(termo),
    );
  }, [alunosParaBusca, buscaAlunoDebounced, alunosSelecionados]);

  const loading = loadingAlunos || loadingMatriculas;
  const mostraResultadosBusca = !!buscaAluno && resultadosBusca.length > 0;

  // Adicionar aluno à lista de selecionados
  const adicionarAluno = (aluno) => {
    setAlunosSelecionados((prev) => [...prev, aluno]);
    setBuscaAluno('');
  };

  // Colar/digitar o nome completo e dar Enter já adiciona, sem precisar
  // clicar na opção do dropdown — usa `buscaAluno` (o valor JÁ digitado,
  // sem esperar o debounce de 300ms) pra não perder o Enter que vem colado
  // junto do paste. Só adiciona automático se achar um match EXATO (nome
  // inteiro igual, ignorando acento/caixa/espaço duplo) ou se houver um
  // único resultado parecido — com mais de um parecido e nenhum exato, não
  // arrisca adicionar o aluno errado, o usuário escolhe manualmente na lista.
  const handleBuscaKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const termo = normalizarBusca(buscaAluno);
    if (!termo) return;
    const idsSelecionados = new Set(alunosSelecionados.map((s) => s.id));
    const candidatos = alunosParaBusca.filter(
      (a) => !idsSelecionados.has(a.id) && normalizarBusca(a.nome).includes(termo),
    );
    const exato = candidatos.find((a) => normalizarBusca(a.nome) === termo);
    const escolhido = exato || (candidatos.length === 1 ? candidatos[0] : null);
    if (escolhido) {
      e.preventDefault();
      adicionarAluno(escolhido);
    }
  };

  // Remover aluno da lista de selecionados
  const removerAluno = (alunoId) => {
    setAlunosSelecionados((prev) => prev.filter((a) => a.id !== alunoId));
  };

  const nenhumFiltroAtivo =
    !atividadeFiltro && !professorFiltro && !diaFiltro && !horarioFiltro && !turnoFiltro;

  // Filtrar alunos com base nos filtros selecionados
  const alunosFiltrados = useMemo(() => {
    // Se há alunos selecionados manualmente, usa eles
    if (alunosSelecionados.length > 0) {
      return alunosSelecionados;
    }

    // Considera apenas matrículas em atividades que não foram encerradas (data_fim IS NULL)
    const matriculasAtivas = matriculas.filter(
      (m) => !atividadePorId.get(String(m.id_atividade))?.data_fim,
    );

    if (atividadeFiltro === '__sem_matricula__') {
      const idsComAlgumaMatricula = new Set(matriculasAtivas.map((m) => m.aluno_id));
      return alunos.filter((a) => !idsComAlgumaMatricula.has(a.id));
    }

    let matriculasFiltradas = [...matriculasAtivas];

    if (atividadeFiltro) {
      matriculasFiltradas = matriculasFiltradas.filter(
        (m) => m.id_atividade === parseInt(atividadeFiltro, 10),
      );
    }
    if (professorFiltro) {
      matriculasFiltradas = matriculasFiltradas.filter(
        (m) =>
          m.nome_professor === professorFiltro ||
          temProfessor(atividadePorId.get(String(m.id_atividade)), professorFiltro),
      );
    }
    if (diaFiltro) {
      matriculasFiltradas = matriculasFiltradas.filter((m) => m.dia_semana === diaFiltro);
    }
    if (horarioFiltro) {
      matriculasFiltradas = matriculasFiltradas.filter((m) => m.horario === horarioFiltro);
    }
    if (turnoFiltro) {
      matriculasFiltradas = matriculasFiltradas.filter((m) => m.turno === turnoFiltro);
    }

    if (nenhumFiltroAtivo) {
      return alunos;
    }

    const alunoIdsFiltrados = new Set(matriculasFiltradas.map((m) => m.aluno_id));
    return alunos.filter((a) => alunoIdsFiltrados.has(a.id));
  }, [
    alunos,
    matriculas,
    atividadePorId,
    temProfessor,
    atividadeFiltro,
    professorFiltro,
    diaFiltro,
    horarioFiltro,
    turnoFiltro,
    alunosSelecionados,
    nenhumFiltroAtivo,
  ]);

  // Criar grade de dados
  const alunosGradeData = useMemo(() => {
    const grid = {};

    alunosFiltrados.forEach((aluno) => {
      grid[aluno.id] = {};

      DIAS_SEMANA.forEach((dia) => {
        HORARIOS.forEach((horario) => {
          const matricula = matriculas.find(
            (m) => m.aluno_id === aluno.id && m.dia_semana === dia && m.horario === horario,
          );
          grid[aluno.id][`${dia}-${horario}`] = matricula || null;
        });
      });
    });

    return grid;
  }, [alunosFiltrados, matriculas]);

  // Mesmo cálculo de "label da célula" usado na renderização da grade (ver
  // JSX abaixo) — extraído aqui pra ser reaproveitado tanto no filtro por
  // coluna quanto no cálculo das opções de cada coluna, sem duplicar a lógica.
  const obterLabelSlot = useCallback(
    (alunoId, dia, horario) => {
      const slot = `${dia}-${horario}`;
      const matricula = alunosGradeData[alunoId]?.[slot];
      const atividadeId = alteracoes[`${alunoId}-${slot}`];
      if (atividadeId !== undefined) {
        if (!atividadeId) return '-';
        const atv = atividadesAtivas.find((a) => String(a.id) === String(atividadeId));
        return atv ? atv.nome : '-';
      }
      return matricula?.nome_atividade || '-';
    },
    [alunosGradeData, alteracoes, atividadesAtivas],
  );

  // Matrículas do aluno indexadas por id — usado só pelas colunas "Educador —
  // <Área>" (ver COLUNAS_EDUCADOR_POR_AREA), pra não percorrer a lista
  // inteira de matrículas pra cada aluno × área na hora de montar a tabela.
  const matriculasPorAlunoId = useMemo(() => {
    const mapa = new Map();
    matriculas.forEach((m) => {
      if (!mapa.has(m.aluno_id)) mapa.set(m.aluno_id, []);
      mapa.get(m.aluno_id).push(m);
    });
    return mapa;
  }, [matriculas]);

  // Nome(s) do(s) educador(es) atual(is) do aluno numa área específica —
  // olha todas as matrículas ativas do aluno e junta o professor de cada uma
  // cuja atividade é daquela área. Mais de uma turma da mesma área (comum em
  // Cultural, por exemplo) pode dar mais de um nome; sem nenhuma matrícula
  // naquela área, mostra "-".
  const obterEducadorArea = useCallback(
    (aluno, areaValue) => {
      const matriculasDoAluno = matriculasPorAlunoId.get(aluno.id) || [];
      const nomes = new Set();
      matriculasDoAluno.forEach((m) => {
        if (areaPorAtividadeId.get(String(m.id_atividade)) === areaValue) {
          if (m.nome_professor) nomes.add(m.nome_professor);
          const atv = atividadePorId.get(String(m.id_atividade));
          if (Array.isArray(atv?.professores_adicionais)) {
            atv.professores_adicionais.forEach((p) => {
              if (p.nome) nomes.add(p.nome);
            });
          }
        }
      });
      return nomes.size > 0
        ? [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR')).join(', ')
        : '-';
    },
    [matriculasPorAlunoId, areaPorAtividadeId, atividadePorId],
  );

  // Ponto único que sabe formatar o valor de QUALQUER coluna extra de aluno
  // (campo simples, campo composto como Nível, ou "Educador — <Área>") —
  // evita repetir essa ramificação nas 3 outras coisas que precisam do valor
  // formatado (opções de filtro, filtro em si, e a célula da tabela).
  const formatarColunaAluno = useCallback(
    (coluna, aluno) => {
      if (coluna.area) return obterEducadorArea(aluno, coluna.area);
      if (coluna.formatarAluno) return coluna.formatarAluno(aluno);
      return coluna.formatar(aluno[coluna.chave]);
    },
    [obterEducadorArea],
  );

  // Opções de cada dropdown de filtro de coluna: os nomes de turma que
  // realmente aparecem naquela coluna (dia+horário), entre os alunos que já
  // passaram pelos filtros de cima — igual ao Excel, só oferece valores que
  // existem de verdade na coluna.
  const opcoesPorColuna = useMemo(() => {
    const mapa = {};
    DIAS_SEMANA.forEach((dia) => {
      HORARIOS.forEach((horario) => {
        const valores = new Set();
        alunosFiltrados.forEach((aluno) => valores.add(obterLabelSlot(aluno.id, dia, horario)));
        mapa[`${dia}-${horario}`] = [...valores].sort((a, b) => a.localeCompare(b, 'pt-BR'));
      });
    });
    return mapa;
  }, [alunosFiltrados, obterLabelSlot]);

  // Área de cada nome de turma que aparece em cada coluna dia+horário — usada
  // só pelo filtro por área dentro do FiltroColuna (ver seção "Área" no
  // painel). Vem de atividadesAtivas (já filtrada por área desligada) casando
  // pelo mesmo dia+horário da coluna, então nomes repetidos em outra
  // combinação dia/horário não vazam área errada aqui.
  const areaPorOpcaoPorColuna = useMemo(() => {
    const mapa = {};
    DIAS_SEMANA.forEach((dia) => {
      HORARIOS.forEach((horario) => {
        const porNome = new Map();
        atividadesAtivas
          .filter((atv) => atv.dia_semana === dia && atv.horario === horario)
          .forEach((atv) => {
            if (atv.area) porNome.set(atv.nome, atv.area);
          });
        mapa[`${dia}-${horario}`] = porNome;
      });
    });
    return mapa;
  }, [atividadesAtivas]);

  // Mesma ideia de opcoesPorColuna, só que pras colunas extras de dados do
  // inscrito (Sexo, Telefone, Nível etc.) em vez das colunas de horário.
  const opcoesPorColunaAluno = useMemo(() => {
    const mapa = {};
    colunasAlunoAtivas.forEach((coluna) => {
      const valores = new Set();
      alunosFiltrados.forEach((aluno) => valores.add(formatarColunaAluno(coluna, aluno)));
      // Idade precisa de ordenação numérica ("2 anos" antes de "10 anos") —
      // ordem alfabética das outras colunas colocaria "10" antes de "2".
      mapa[coluna.chave] =
        coluna.chave === 'idade'
          ? [...valores].sort(
              (a, b) => (parseInt(a, 10) || Infinity) - (parseInt(b, 10) || Infinity),
            )
          : [...valores].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    });
    return mapa;
  }, [alunosFiltrados, colunasAlunoAtivas, formatarColunaAluno]);

  // Valor exibido de um aluno numa coluna de filtro, seja ela uma coluna de
  // horário ("dia-horario") ou uma coluna extra de dados do inscrito — usado
  // só pelo filtro, por isso fica genérico em vez de duplicar em cada lugar
  // que já sabe formatar seu próprio tipo de coluna.
  const obterValorColuna = useCallback(
    (aluno, chave) => {
      const colunaAluno = TODAS_COLUNAS_ALUNO.find((c) => c.chave === chave);
      if (colunaAluno) return formatarColunaAluno(colunaAluno, aluno);
      const [dia, horario] = chave.split('-');
      return obterLabelSlot(aluno.id, dia, horario);
    },
    [formatarColunaAluno, obterLabelSlot],
  );

  const totalFiltrosColunaAtivos = Object.values(filtrosColuna).filter(
    (valores) => valores && valores.length > 0,
  ).length;

  // Aplica os filtros de coluna (E lógico entre colunas diferentes, OU dentro
  // dos valores marcados de uma mesma coluna) por cima do que os filtros de
  // cima já selecionaram.
  const alunosExibidos = useMemo(() => {
    const ativos = Object.entries(filtrosColuna).filter(
      ([, valores]) => valores && valores.length > 0,
    );
    if (ativos.length === 0) return alunosFiltrados;
    const idsComAlgumaMatricula = new Set(matriculas.map((m) => m.aluno_id));
    return alunosFiltrados.filter((aluno) => {
      const semNenhumaMatricula =
        atividadeFiltro === '__sem_matricula__' && !idsComAlgumaMatricula.has(aluno.id);
      return ativos.every(
        ([chave, valores]) =>
          (semNenhumaMatricula && HORARIO_CHAVES.has(chave)) ||
          valores.includes(obterValorColuna(aluno, chave)),
      );
    });
  }, [alunosFiltrados, filtrosColuna, obterValorColuna, matriculas, atividadeFiltro]);

  // Paginação local sobre os alunosExibidos (mantém filtros e exportação globais)
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(50);

  useEffect(() => {
    setPaginaAtual(1);
  }, [
    atividadeFiltro,
    professorFiltro,
    diaFiltro,
    horarioFiltro,
    turnoFiltro,
    alunosSelecionados,
    filtrosColuna,
    itensPorPagina,
  ]);

  const totalPaginas = useMemo(() => {
    if (itensPorPagina === 0) return 1;
    return Math.max(1, Math.ceil(alunosExibidos.length / itensPorPagina));
  }, [alunosExibidos.length, itensPorPagina]);

  const paginaSegura = Math.min(paginaAtual, totalPaginas);

  const alunosPaginados = useMemo(() => {
    if (itensPorPagina === 0) return alunosExibidos;
    const inicio = (paginaSegura - 1) * itensPorPagina;
    return alunosExibidos.slice(inicio, inicio + itensPorPagina);
  }, [alunosExibidos, paginaSegura, itensPorPagina]);

  // Exporta em Excel (nome/numero) só os inscritos EXIBIDOS no momento —
  // `alunosExibidos` já reflete todos os filtros ativos (topo + coluna) e a
  // seleção manual, então é exatamente "o que o usuário está vendo agora".
  // Telefone vem numa chamada à parte (GET /api/alunos/telefones), não do
  // fetch geral de alunos, pra não pesar o join com responsavel_legal em toda
  // tela que usa esse endpoint — só busca aqui, e só pros IDs visíveis.
  const [exportando, setExportando] = useState(false);
  const exportarExcel = async () => {
    if (alunosExibidos.length === 0) return;
    setExportando(true);
    try {
      const ids = alunosExibidos.map((a) => a.id);
      const res = await fetch(`${API_URL}/api/alunos/telefones?ids=${ids.join(',')}`, {
        headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erro ao buscar telefones para exportar.');
      const rawTelefones = await res.json();
      const telefones = rawTelefones?.data ?? (Array.isArray(rawTelefones) ? rawTelefones : []);
      const telefonePorId = new Map(telefones.map((t) => [t.id, t]));

      const linhas = alunosExibidos.map((a) => {
        const t = telefonePorId.get(a.id);
        return { nome: a.nome, numero: t?.telefone_responsavel || t?.telefone_aluno || '' };
      });
      const worksheet = XLSX.utils.json_to_sheet(linhas);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ajuste Grade');
      XLSX.writeFile(workbook, 'AjusteGrade.xlsx');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExportando(false);
    }
  };

  // Recalcula o indicador de rolagem quando a grade aparece/muda de tamanho
  // (nova busca, filtro etc.) — sem isso a barra ficaria com o valor inicial
  // (0%, 100% de largura) até o usuário rolar pela primeira vez.
  useEffect(() => {
    const frame = requestAnimationFrame(recalcularBarraScroll);
    return () => cancelAnimationFrame(frame);
  }, [alunosExibidos, recalcularBarraScroll]);

  // useCallback aqui não é só estilo: FiltroColuna é memoizado (React.memo)
  // pra não reexecutar de novo a cada edição de célula alheia — se essa
  // função ganhasse uma referência nova em todo render do componente-pai,
  // o memo do FiltroColuna seria inútil (prop "nova" a cada vez).
  const handleFiltrarColuna = useCallback(
    (chave, valores) => {
      setFiltrosColuna((prev) => {
        const novo = { ...prev };
        if (valores && valores.length > 0) novo[chave] = valores;
        else delete novo[chave];
        return novo;
      });
    },
    [setFiltrosColuna],
  );

  // Turno real do aluno: vem do cadastro dele (a.turno, já incluído em
  // GET /api/alunos), não da primeira matrícula que aparecer. Antes pegava
  // o turno de "qualquer matrícula com turno" — se essa matrícula fosse um
  // ensaio (turno "Noite", sempre permitido pra qualquer aluno, ver
  // podeMatricular em regras-matricula.js), o aluno inteiro ficava marcado
  // como "Noite" e nenhuma célula dele mostrava as turmas do turno real.
  // Só cai pra uma matrícula como último recurso (aluno sem turno
  // cadastrado, caso raro) — e mesmo assim evita pegar logo uma de ensaio.
  const turnoDoAluno = useMemo(() => {
    const turnoMap = {};

    alunos.forEach((aluno) => {
      if (aluno.turno) {
        turnoMap[aluno.id] = aluno.turno;
        return;
      }
      const matriculaDoAluno =
        matriculas.find((m) => m.aluno_id === aluno.id && m.turno && m.turno !== 'Noite') ||
        matriculas.find((m) => m.aluno_id === aluno.id && m.turno);
      if (matriculaDoAluno) {
        turnoMap[aluno.id] = matriculaDoAluno.turno;
      }
    });

    return turnoMap;
  }, [alunos, matriculas]);

  // Contagem simplificada para os selects da grade
  const contagemAtividadesPorHorario = useMemo(() => {
    const contagem = {};
    matriculas.forEach((matricula) => {
      const key = `${matricula.id_atividade}-${matricula.dia_semana}-${matricula.horario}-${matricula.turno}`;
      contagem[key] = (contagem[key] || 0) + 1;
    });
    return contagem;
  }, [matriculas]);

  // Atualizar alteração
  const handleAlteracao = (alunoId, slot, atividadeId) => {
    setAlteracoes((prev) => ({
      ...prev,
      [`${alunoId}-${slot}`]: atividadeId,
    }));
  };

  // Fatia de `alteracoes` por aluno, com referência ESTÁVEL pra quem não foi
  // tocado. `alteracoes` é um objeto plano só (chave "alunoId-dia-horario")
  // porque é assim que handleSalvar já sabe montar o payload — não vale a
  // pena mudar esse formato só por performance. Mas isso significa que toda
  // edição de UMA célula troca a referência do objeto `alteracoes` inteiro, e
  // se cada linha da grade (LinhaAluno, memoizado) recebesse esse objeto
  // direto, TODAS as ~linhas re-renderizariam a cada seleção, não só a linha
  // editada — exatamente o gargalo que essa mudança existe pra evitar. Aqui
  // reconstruímos por aluno, mas reaproveitando (por referência) a fatia de
  // quem não mudou, via um cache num ref — só o aluno realmente editado troca
  // de referência; os outros continuam apontando pro mesmo objeto de antes, o
  // que faz o React.memo da linha dele pular o re-render de verdade.
  const alteracoesPorAlunoRef = useRef(new Map());
  const alteracoesPorAluno = useMemo(() => {
    const novasPorAluno = new Map();
    Object.entries(alteracoes).forEach(([chave, valor]) => {
      const alunoId = chave.split('-')[0];
      if (!novasPorAluno.has(alunoId)) novasPorAluno.set(alunoId, {});
      novasPorAluno.get(alunoId)[chave] = valor;
    });
    const cache = alteracoesPorAlunoRef.current;
    const resultado = new Map();
    novasPorAluno.forEach((novo, alunoId) => {
      const antigo = cache.get(alunoId);
      const chavesNovo = Object.keys(novo);
      const igual =
        antigo &&
        chavesNovo.length === Object.keys(antigo).length &&
        chavesNovo.every((k) => antigo[k] === novo[k]);
      resultado.set(alunoId, igual ? antigo : novo);
    });
    alteracoesPorAlunoRef.current = resultado;
    return resultado;
  }, [alteracoes]);

  // Modal de busca pra trocar a turma de uma célula — substitui o <select>
  // nativo porque a lista de turmas do mesmo dia/horário/turno pode ser longa
  // (mesmo padrão do "mover aluno" em GradeTurmas.js). Um único modal
  // compartilhado por toda a grade, aberto pra célula { alunoId, dia, horario,
  // turno } de cada vez.
  const [celulaEmEdicao, setCelulaEmEdicao] = useState(null);
  const [buscaTurmaCelula, setBuscaTurmaCelula] = useState('');
  // "+ Nova turma" dentro do painel de escolher turma da célula — dia/horário
  // já vêm de `celulaEmEdicao` (a turma nasce exatamente nesse slot), só
  // falta nome/educador/área. Turno vem pré-marcado com o do próprio aluno
  // (caso comum), mas continua editável — precisa poder escolher "Noite" pra
  // criar um ensaio direto daqui, por exemplo, sem que isso feche a tela.
  const [criandoTurma, setCriandoTurma] = useState(false);
  const [novaTurmaForm, setNovaTurmaForm] = useState({
    nome: '',
    professor_nome: '',
    area: 'educacional',
    turno: '',
  });
  const [salvandoNovaTurma, setSalvandoNovaTurma] = useState(false);

  // useCallback pela mesma razão de handleFiltrarColuna: é passada pra
  // LinhaAluno (memoizado), então precisa de referência estável entre
  // renders pra o memo não virar letra morta.
  const abrirCelula = useCallback((alunoId, dia, horario, turno) => {
    setCelulaEmEdicao({ alunoId, dia, horario, turno });
    setBuscaTurmaCelula('');
    setCriandoTurma(false);
    // aluno.turno vem em MAIÚSCULAS do cadastro ("MANHÃ"/"TARDE") — normaliza
    // pro valor exato das opções do <select>, senão o padrão aparece vazio.
    const turnoPadrao =
      TURNOS.find(
        (t) =>
          t.toLowerCase() ===
          String(turno || '')
            .trim()
            .toLowerCase(),
      ) || '';
    setNovaTurmaForm({ nome: '', professor_nome: '', area: 'educacional', turno: turnoPadrao });
  }, []);

  // Trocar o turno direto na grade — mesma regra da tela de editar aluno
  // (CadastrarAluno.js): avisa e pede confirmação ANTES de mandar, listando
  // as matrículas atuais que ficariam incompatíveis (o backend já encerra
  // elas automaticamente ao salvar, ver encerrarMatriculasForaDoTurno em
  // backend/status-sync.js — ensaio/turno "Noite" nunca entra nessa lista).
  const onMudarTurno = useCallback(
    async (aluno, novoTurno) => {
      const matriculasDoAluno = matriculas.filter((m) => m.aluno_id === aluno.id);
      const incompativeis = matriculasDoAluno.filter((m) => !compativelTurno(novoTurno, m.turno));
      if (incompativeis.length > 0) {
        const lista = incompativeis
          .map((m) => `• ${m.nome_atividade} (${m.dia_semana} ${m.horario})`)
          .join('\n');
        const confirmou = await confirm(
          `Ao mudar o turno de ${aluno.nome}, as seguintes matrículas atuais não serão mais compatíveis e serão encerradas (histórico continua registrado, pode reabrir depois):\n\n${lista}\n\nEnsaios (turno Noite) não são afetados. Deseja continuar?`,
          { title: 'Confirmar troca de turno', confirmText: 'Continuar', tone: 'danger' },
        );
        if (!confirmou) return;
      }
      try {
        const res = await fetch(`${API_URL}/api/alunos/${aluno.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-institution-id': institutionId,
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ campo: 'turno', valor: novoTurno }),
        });
        const rawData = await res.json();
        const data = rawData?.data ?? rawData;
        if (!res.ok) throw new Error(rawData?.error || 'Erro ao mudar o turno.');
        if (data.matriculas_encerradas > 0) {
          toast.success(
            `Turno de ${aluno.nome} atualizado! ${data.matriculas_encerradas} matrícula(s) encerrada(s) pela troca de turno.`,
            8000,
          );
        } else {
          toast.success(`Turno de ${aluno.nome} atualizado.`);
        }
        // Aplica no cache exatamente o que o backend acabou de fazer, em vez de
        // invalidar e rebaixar `alunos`/`matriculas` inteiros (centenas de
        // alunos, milhares de matrículas) só por causa de 1 aluno — em internet
        // ruim isso travava a tela inteira a cada troca de turno. `incompativeis`
        // já foi calculado acima com a MESMA regra do backend
        // (encerrarMatriculasForaDoTurno), então já sabemos exatamente o que mudou.
        if (data.matriculas_encerradas === incompativeis.length) {
          queryClient.setQueryData(['alunos', institutionId], (old) =>
            Array.isArray(old)
              ? old.map((a) => (a.id === aluno.id ? { ...a, turno: novoTurno } : a))
              : old,
          );
          if (incompativeis.length > 0) {
            const idsEncerradas = new Set(incompativeis.map((m) => m.id));
            queryClient.setQueryData(['matriculas', institutionId], (old) =>
              Array.isArray(old) ? old.filter((m) => !idsEncerradas.has(m.id)) : old,
            );
          }
        } else {
          // Discrepância entre o que a gente previu e o que o backend de fato
          // encerrou (ex.: alguma matrícula mudou entre calcular `incompativeis`
          // e o PATCH terminar) — cai pro caminho seguro de sempre, recarrega tudo.
          await queryClient.invalidateQueries({ queryKey: ['alunos', institutionId] });
          await queryClient.invalidateQueries({ queryKey: ['matriculas', institutionId] });
        }
      } catch (err) {
        toast.error(err.message);
      }
    },
    [matriculas, confirm, toast, queryClient, institutionId, token],
  );

  const opcoesCelulaEmEdicao = useMemo(() => {
    if (!celulaEmEdicao) return [];
    const { dia, horario, turno } = celulaEmEdicao;
    const termo = buscaTurmaCelula.trim().toLowerCase();
    // `alunos.turno` vem do cadastro em MAIÚSCULAS ("TARDE", "MANHÃ") mas
    // `atividades.turno` é capitalizado ("Tarde", "Manhã") — comparação tem
    // que ignorar caixa, senão nunca bate (mesmo cuidado que o backend já
    // tem em podeMatricular, regras-matricula.js).
    const turnoNorm = turno ? turno.toLowerCase() : '';
    const ehNoite = (t) => String(t).toLowerCase() === 'noite';
    return (
      atividadesAtivas
        // Mesma regra de compatibilidade do backend (podeMatricular): turma do
        // turno do próprio aluno, OU turma de ensaio (turno "Noite" — sempre
        // permitido, independente do turno do aluno). Antes era igualdade
        // estrita com o turno do aluno, escondendo tanto os ensaios daquele
        // dia/horário quanto (por causa da diferença de caixa) as turmas do
        // próprio turno do aluno.
        .filter(
          (atv) =>
            atv.dia_semana === dia &&
            atv.horario === horario &&
            (!turnoNorm || String(atv.turno).toLowerCase() === turnoNorm || ehNoite(atv.turno)),
        )
        .map((atv) => ({
          value: atv.id,
          label: atv.nome,
          professor: atv.nome_professor || '',
          area: atv.area ? AREA_LABEL[atv.area] || atv.area : '',
          // Só marca quando destoa do turno do próprio aluno (o ensaio incluído
          // acima) — evita marcar toda linha quando a lista é só do turno dele.
          ensaioNoturno: ehNoite(atv.turno) && turnoNorm !== 'noite',
          count: contagemAtividadesPorHorario[`${atv.id}-${dia}-${horario}-${atv.turno}`] || 0,
        }))
        .filter(
          (o) =>
            !termo ||
            o.label.toLowerCase().includes(termo) ||
            o.professor.toLowerCase().includes(termo) ||
            o.area.toLowerCase().includes(termo),
        )
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
    );
  }, [celulaEmEdicao, buscaTurmaCelula, atividadesAtivas, contagemAtividadesPorHorario]);

  const escolherTurmaCelula = (atividadeId) => {
    const { alunoId, dia, horario } = celulaEmEdicao;
    handleAlteracao(alunoId, `${dia}-${horario}`, atividadeId ? String(atividadeId) : '');
    setCelulaEmEdicao(null);
  };

  // Cria a turma direto no dia/horário/turno da célula que já estava sendo
  // editada, e já deixa escolhida pro aluno em seguida (mesmo efeito de
  // clicar numa opção existente) — sem isso o coordenador precisava sair pra
  // Turmas.js, criar, voltar e reabrir a célula pra achar a turma nova.
  const criarTurmaCelula = async () => {
    const nome = novaTurmaForm.nome.trim();
    if (!nome) {
      toast.error('Informe o nome da turma.');
      return;
    }
    if (!novaTurmaForm.turno) {
      toast.error('Escolha o turno da turma.');
      return;
    }

    setSalvandoNovaTurma(true);
    try {
      const res = await fetch(`${API_URL}/api/atividades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nome,
          professor_nome: novaTurmaForm.professor_nome.trim(),
          dia_semana: celulaEmEdicao.dia,
          horario: celulaEmEdicao.horario,
          turno: novaTurmaForm.turno,
          area: novaTurmaForm.area,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao criar turma.');

      // Acrescenta a turma nova direto no cache (a resposta do POST já traz
      // tudo que precisamos) em vez de invalidar e recarregar a lista inteira
      // de turmas da instituição — mesmo espírito da troca de turno acima.
      queryClient.setQueryData(['atividades', institutionId], (old) => {
        const item = data?.data ?? data;
        const novaTurma = {
          ...item,
          nome_professor: novaTurmaForm.professor_nome.trim() || null,
          total_alunos: 0,
          professores_adicionais: [],
        };
        return Array.isArray(old) ? [...old, novaTurma] : [novaTurma];
      });

      toast.success(`Turma "${nome}" criada e atribuída.`);
      escolherTurmaCelula(data.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvandoNovaTurma(false);
    }
  };

  // Salvar alterações em lote
  const handleSalvar = async () => {
    if (Object.keys(alteracoes).length === 0) {
      toast.info('Nenhuma alteração para salvar.');
      return;
    }

    setSalvando(true);

    try {
      const updates = [];

      Object.entries(alteracoes).forEach(([key, atividadeId]) => {
        // Chave no formato "alunoId-dia-horario" (ver handleAlteracao). Como
        // "dia" e "horario" também usam "-" internamente (ex.: "HR-1" viria de
        // HORARIOS, e nomes de dia não têm hífen, mas o parsing é feito de forma
        // genérica): separa pelo primeiro "-" (alunoId) e pelo ÚLTIMO "-" (horario),
        // o meio sobra pra "dia".
        const parts = key.split('-');
        const alunoId = parts[0];
        const slot = parts.slice(1).join('-');
        const lastSpaceIndex = slot.lastIndexOf('-');
        const dia = slot.substring(0, lastSpaceIndex);
        const horario = slot.substring(lastSpaceIndex + 1);

        updates.push({
          aluno_id: parseInt(alunoId),
          dia_semana: dia,
          horario: horario,
          id_atividade: atividadeId ? parseInt(atividadeId) : null,
        });
      });

      const response = await fetch(`${API_URL}/api/matriculas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ alteracoes: updates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao salvar alterações');
      }

      const body = await response.json();
      const result = body?.data ?? body;

      if (result.conflitos_turno && result.conflitos_turno.length > 0) {
        toast.warning(
          `${result.conflitos_turno.length} célula(s) ignorada(s) por conflito de turno:\n${result.conflitos_turno.join('\n')}`,
          8000,
        );
      }
      if (result.duplicatas_resolvidas > 0) {
        toast.info(
          `${result.duplicatas_resolvidas} matrícula(s) duplicada(s) antiga(s) foram encontradas e encerradas automaticamente nessa(s) célula(s).`,
          8000,
        );
      }
      toast.success(`${result.updated} alterações salvas com sucesso!`);
      setAlteracoes({});
      // Sintaxe antiga do react-query v3 (array posicional) não invalida nada
      // de verdade nesta versão — precisa do objeto `{ queryKey }` (v4/v5,
      // igual todas as outras invalidações deste arquivo). Sem isso, o
      // backend salvava certo, mas a grade continuava mostrando a turma/
      // professor antigo até um F5, porque o cache de `matriculas` nunca era
      // marcado como obsoleto.
      await queryClient.invalidateQueries({ queryKey: ['matriculas'] });
    } catch (err) {
      console.error('Erro ao salvar alterações:', err);
      toast.error('Erro ao salvar alterações: ' + err.message);
    } finally {
      setSalvando(false);
    }
  };

  if (!podeEditar) {
    return (
      <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 flex items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Você não tem permissão para ajustar a grade.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="px-3 py-1.5 max-w-full mx-auto space-y-1.5">
        {/* Barra de Filtros e Ações Ultra-Compacta */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm px-2.5 py-1.5 space-y-1">
          {/* Linha 1: Filtros de busca + Ações rápidas + Salvar */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" title="Filtros" />

            <div className="relative flex-1 min-w-[130px] max-w-[220px]">
              <Input
                size="xs"
                icon={Search}
                value={buscaAluno}
                onChange={(e) => setBuscaAluno(e.target.value)}
                onKeyDown={handleBuscaKeyDown}
                placeholder="Buscar inscrito..."
              />
              {mostraResultadosBusca && (
                <div className="absolute z-30 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-auto">
                  {resultadosBusca.map((aluno) => {
                    const corNivel = corCardNivel(aluno.nivel);
                    return (
                      <button
                        key={aluno.id}
                        onClick={() => adicionarAluno(aluno)}
                        className={`w-full px-3 py-1.5 text-left text-xs text-gray-700 dark:text-gray-300 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${corNivel ? `${corNivel} hover:brightness-95 dark:hover:brightness-110` : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                        translate="no"
                      >
                        {aluno.nome}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Select
              size="xs"
              className="w-36 flex-shrink-0"
              value={atividadeFiltro}
              onChange={(e) => setAtividadeFiltro(e.target.value)}
              options={[
                { value: '', label: 'Atividade: Todas' },
                { value: '__sem_matricula__', label: '⚠️ Sem matrícula' },
                ...opcoesAtividade,
              ]}
            />
            <Select
              size="xs"
              className="w-28 flex-shrink-0"
              value={professorFiltro}
              onChange={(e) => setProfessorFiltro(e.target.value)}
              options={[
                { value: '', label: 'Educador: Todos' },
                ...opcoesProfessor.map((prof) => ({ value: prof, label: prof })),
              ]}
            />
            <Select
              size="xs"
              className="w-24 flex-shrink-0"
              value={diaFiltro}
              onChange={(e) => setDiaFiltro(e.target.value)}
              options={[
                { value: '', label: 'Dia: Todos' },
                ...opcoesDia.map((dia) => ({ value: dia, label: dia })),
              ]}
            />
            <Select
              size="xs"
              className="w-24 flex-shrink-0"
              value={horarioFiltro}
              onChange={(e) => setHorarioFiltro(e.target.value)}
              options={[
                { value: '', label: 'Horário: Todos' },
                ...opcoesHorario.map((h) => ({ value: h, label: h })),
              ]}
            />
            <Select
              size="xs"
              className="w-24 flex-shrink-0"
              value={turnoFiltro}
              onChange={(e) => setTurnoFiltro(e.target.value)}
              options={[
                { value: '', label: 'Turno: Todos' },
                ...opcoesTurno.map((t) => ({ value: t, label: t })),
              ]}
            />

            <button
              onClick={limparFiltros}
              className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline px-1"
              title="Limpar todos os filtros"
            >
              Limpar
            </button>

            {duplicidades.length > 0 && (
              <button
                type="button"
                onClick={() => setDuplicidadesModalAberto(true)}
                className="inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              >
                <AlertTriangle className="w-3 h-3" />
                Duplicidades
                <span className="px-1 rounded bg-amber-100 dark:bg-amber-900/50">
                  {duplicidades.length}
                </span>
              </button>
            )}

            {podeExportar && (
              <button
                type="button"
                onClick={exportarExcel}
                disabled={exportando || alunosExibidos.length === 0}
                title="Exporta nome e número só de quem está visível com o filtro atual"
                className="inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {exportando ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Download className="w-3 h-3" />
                )}
                Excel
              </button>
            )}

            <div className="relative" ref={painelColunasRef}>
              <button
                type="button"
                onClick={() => setPainelColunasAberto((prev) => !prev)}
                className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded-lg border transition-colors ${
                  painelColunasAberto || colunasAlunoAtivas.length > 0 || diasOcultos > 0
                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <Columns3 className="w-3 h-3" />
                Colunas
                {(colunasAlunoAtivas.length > 0 || diasOcultos > 0) && (
                  <span className="px-1 rounded bg-blue-100 dark:bg-blue-900/50 text-[10px]">
                    {colunasAlunoAtivas.length + diasOcultos}
                  </span>
                )}
              </button>

              {painelColunasAberto && (
                <div className="absolute z-50 mt-1 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 space-y-3 max-h-96 overflow-auto">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
                      Dados do inscrito
                    </p>
                    <div className="space-y-1">
                      {TODAS_COLUNAS_ALUNO.map((coluna) => (
                        <label
                          key={coluna.chave}
                          className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={!!colunasAlunoVisiveis[coluna.chave]}
                            onChange={() => alternarColunaAluno(coluna.chave)}
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500/30"
                          />
                          {coluna.area
                            ? `Educador — ${AREA_LABEL[coluna.area] || coluna.area}`
                            : coluna.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
                      Dias da semana
                    </p>
                    <div className="space-y-1">
                      {DIAS_SEMANA.map((dia) => (
                        <label
                          key={dia}
                          className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={diasVisiveis[dia] !== false}
                            onChange={() => alternarDiaVisivel(dia)}
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500/30"
                          />
                          {dia}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Áreas e Salvar alinhados à direita na mesma barra */}
            <div className="flex flex-wrap items-center gap-1 ml-auto">
              {AREAS.map((a) => {
                const ativa = areasAtivas[a.value] !== false;
                return (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => alternarArea(a.value)}
                    className={`h-6 px-2 text-[11px] font-semibold rounded-lg transition-colors ${ativa ? a.ativo : a.inativo}`}
                    title={ativa ? `Ocultar turmas de ${a.label}` : `Mostrar turmas de ${a.label}`}
                  >
                    {a.label}
                  </button>
                );
              })}

              <button
                type="button"
                onClick={handleSalvar}
                disabled={salvando || Object.keys(alteracoes).length === 0}
                className="inline-flex items-center justify-center rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed gap-1 px-2.5 h-6 text-[11px] bg-blue-600 hover:bg-blue-700 text-white ml-1"
              >
                {salvando ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                {salvando ? 'Salvando...' : `Salvar (${Object.keys(alteracoes).length})`}
              </button>
            </div>
          </div>

          {/* Chips de alunos selecionados individualmente (só aparece se houver algum) */}
          {alunosSelecionados.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 pt-0.5">
              {alunosSelecionados.map((aluno) => (
                <div
                  key={aluno.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-900/50 rounded-md text-[11px] text-blue-700 dark:text-blue-400"
                >
                  <span translate="no">{aluno.nome}</span>
                  <button
                    onClick={() => removerAluno(aluno.id)}
                    className="hover:text-blue-900 dark:hover:text-blue-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Grade */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-3" />
            Carregando grade...
          </div>
        ) : alunosFiltrados.length > 0 ? (
          <>
            {/* Indicador de posição da rolagem horizontal — sempre visível
                acima da tabela (a scrollbar nativa, mesmo destacada, só
                aparece embaixo do que está visível na tela). O trecho
                colorido mostra proporcionalmente onde a área visível está
                dentro da largura total da grade. */}
            <div
              className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden cursor-pointer select-none"
              onMouseDown={handleBarraMouseDown}
              title="Clique ou arraste para rolar a grade"
            >
              <div
                ref={scrollBarRef}
                className="h-full bg-blue-500 rounded-full transition-[margin-left] duration-75"
                style={{ width: '100%', marginLeft: '0%' }}
              />
            </div>
            {/* max-h + overflow-auto (em vez de só overflow-x-auto): sem altura
                limitada, o container nunca rola de verdade na vertical — e mesmo
                assim, por ter overflow-x definido, o navegador calcula overflow-y
                como "auto" por baixo dos panos, o que faz o sticky top-0 do
                cabeçalho ficar preso a ESSE container (que não rola) em vez da
                página. Limitando a altura, o container passa a rolar de verdade
                e o cabeçalho gruda nele corretamente. */}
            <Card
              ref={gradeScrollRef}
              onScroll={handleScrollGrade}
              onWheel={handleWheelGrade}
              padding="none"
              className="overflow-auto max-h-[calc(100vh-145px)] scroll-destacado"
            >
              <table className="w-full text-xs border-collapse">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {/* Canto (Aluno): precisa travar nos DOIS eixos (linha no topo ao rolar
                      verticalmente, coluna à esquerda ao rolar horizontalmente) — por isso
                      z-20, mais alto que as outras células sticky (z-10) que só travam num eixo. */}
                    {/* box-shadow INSET em vez de confiar só no border-right: com
                      border-collapse não existe espaço nenhum entre células vizinhas,
                      então uma sombra "pra fora" (offset positivo) acaba pintada por
                      baixo da célula ao lado e nunca aparece; inset desenha a linha
                      DENTRO do próprio box da célula sticky, que sempre pinta por
                      cima do conteúdo que rola por baixo dela. */}
                    <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 sticky top-0 left-0 bg-gray-50 dark:bg-gray-800 min-w-[190px] border border-gray-200 dark:border-gray-700 shadow-[inset_-2px_0_0_0_#e5e7eb] dark:shadow-[inset_-2px_0_0_0_#374151] z-20">
                      Inscrito
                      <span
                        className="ml-1.5 text-[10px] font-normal text-gray-400 dark:text-gray-500 normal-case"
                        title="Linhas exibidas com os filtros atuais (de cima e de coluna)"
                      >
                        ({alunosExibidos.length})
                      </span>
                      {totalFiltrosColunaAtivos > 0 && (
                        <button
                          type="button"
                          onClick={() => setFiltrosColuna({})}
                          title="Limpar filtros de coluna"
                          className="ml-2 text-[10px] font-normal text-blue-600 dark:text-blue-400 hover:underline normal-case"
                        >
                          limpar filtros ({totalFiltrosColunaAtivos})
                        </button>
                      )}
                    </th>
                    {colunasAlunoAtivas.map((coluna) => {
                      const opcoes = opcoesPorColunaAluno[coluna.chave] || [];
                      const selecionados = filtrosColuna[coluna.chave] || [];
                      return (
                        <th
                          key={coluna.chave}
                          className="px-2 py-1.5 text-left font-medium text-gray-600 dark:text-gray-300 min-w-[110px] border border-gray-200 dark:border-gray-700 sticky top-0 bg-gray-50 dark:bg-gray-800 z-10 whitespace-nowrap"
                        >
                          <div className="mb-1">
                            {coluna.area
                              ? `Educador — ${AREA_LABEL[coluna.area] || coluna.area}`
                              : coluna.label}
                          </div>
                          <FiltroColuna
                            chave={coluna.chave}
                            opcoes={opcoes}
                            selecionados={selecionados}
                            onChange={handleFiltrarColuna}
                            filtrosAbertosRef={filtrosAbertosRef}
                          />
                        </th>
                      );
                    })}
                    {diasExibidos.map((dia) =>
                      HORARIOS.map((horario) => {
                        const chave = `${dia}-${horario}`;
                        const opcoes = opcoesPorColuna[chave] || [];
                        const selecionados = filtrosColuna[chave] || [];
                        return (
                          <th
                            key={chave}
                            className="px-2 py-0.5 text-center font-medium text-gray-600 dark:text-gray-300 min-w-[100px] border border-gray-200 dark:border-gray-700 sticky top-0 bg-gray-50 dark:bg-gray-800 z-10"
                          >
                            <div className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5 truncate">
                              {dia} · {horario}
                            </div>
                            {/* Filtro por coluna, igual Excel: só mostra os nomes de
                              turma que existem de verdade nessa coluna, e permite
                              marcar mais de uma opção ao mesmo tempo. */}
                            <FiltroColuna
                              chave={chave}
                              opcoes={opcoes}
                              selecionados={selecionados}
                              onChange={handleFiltrarColuna}
                              filtrosAbertosRef={filtrosAbertosRef}
                              areaPorOpcao={areaPorOpcaoPorColuna[chave]}
                            />
                          </th>
                        );
                      }),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {alunosExibidos.length === 0 && (
                    <tr>
                      <td
                        colSpan={
                          1 + colunasAlunoAtivas.length + diasExibidos.length * HORARIOS.length
                        }
                        className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                      >
                        Nenhum inscrito corresponde aos filtros de coluna selecionados.
                      </td>
                    </tr>
                  )}
                  {alunosPaginados.map((aluno, idx) => (
                    <LinhaAluno
                      key={aluno.id}
                      aluno={aluno}
                      idx={idx}
                      colunasAlunoAtivas={colunasAlunoAtivas}
                      formatarColunaAluno={formatarColunaAluno}
                      diasExibidos={diasExibidos}
                      matriculaSlots={alunosGradeData[aluno.id]}
                      alteracoesDoAluno={alteracoesPorAluno.get(String(aluno.id))}
                      turno={turnoDoAluno[aluno.id] || ''}
                      atividadesAtivas={atividadesAtivas}
                      areaPorAtividadeId={areaPorAtividadeId}
                      areasAtivas={areasAtivas}
                      abrirCelula={abrirCelula}
                      onMudarTurno={onMudarTurno}
                    />
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Barra de paginação local */}
            {alunosExibidos.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <span>
                    Mostrando{' '}
                    <strong className="text-gray-900 dark:text-gray-100">
                      {itensPorPagina === 0
                        ? 1
                        : (paginaSegura - 1) * itensPorPagina + 1}
                    </strong>{' '}
                    a{' '}
                    <strong className="text-gray-900 dark:text-gray-100">
                      {itensPorPagina === 0
                        ? alunosExibidos.length
                        : Math.min(paginaSegura * itensPorPagina, alunosExibidos.length)}
                    </strong>{' '}
                    de{' '}
                    <strong className="text-gray-900 dark:text-gray-100">
                      {alunosExibidos.length}
                    </strong>{' '}
                    inscrito{alunosExibidos.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5">
                    <span>Por página:</span>
                    <select
                      value={itensPorPagina}
                      onChange={(e) => setItensPorPagina(Number(e.target.value))}
                      className="px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={0}>Todos</option>
                    </select>
                  </label>

                  {totalPaginas > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                        disabled={paginaSegura <= 1}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        Anterior
                      </button>
                      <span className="px-2 font-medium text-gray-700 dark:text-gray-300">
                        Página {paginaSegura} de {totalPaginas}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                        disabled={paginaSegura >= totalPaginas}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                      >
                        Próxima
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
            <p className="text-sm">Nenhum inscrito encontrado com os filtros selecionados.</p>
          </div>
        )}
      </main>

      {/* Modal: buscar turma pra essa célula (aluno + dia + horário) */}
      <Modal
        isOpen={!!celulaEmEdicao}
        onClose={() => setCelulaEmEdicao(null)}
        title={
          celulaEmEdicao
            ? `${celulaEmEdicao.dia} · ${celulaEmEdicao.horario} (${celulaEmEdicao.turno})`
            : 'Escolher turma'
        }
        maxWidth="md"
      >
        {celulaEmEdicao && (
          <div className="space-y-3">
            {criandoTurma ? (
              <div className="space-y-3 p-3 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/40 dark:bg-blue-900/10">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Nova turma em {celulaEmEdicao.dia} · {celulaEmEdicao.horario} — já entra escolhida
                  pra esse aluno ao salvar.
                </p>
                <Input
                  label="Nome da turma"
                  value={novaTurmaForm.nome}
                  onChange={(e) => setNovaTurmaForm({ ...novaTurmaForm, nome: e.target.value })}
                  autoFocus
                />
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Educador
                  </label>
                  <input
                    list="lista-professores-ajuste-grade"
                    value={novaTurmaForm.professor_nome}
                    onChange={(e) =>
                      setNovaTurmaForm({ ...novaTurmaForm, professor_nome: e.target.value })
                    }
                    placeholder="Nome do educador (existente ou novo)"
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                  <datalist id="lista-professores-ajuste-grade">
                    {professoresExistentes.map((nome) => (
                      <option key={nome} value={nome} />
                    ))}
                  </datalist>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Turno"
                    value={novaTurmaForm.turno}
                    onChange={(e) => setNovaTurmaForm({ ...novaTurmaForm, turno: e.target.value })}
                    options={[
                      { value: '', label: 'Selecione...' },
                      ...TURNOS.map((t) => ({ value: t, label: t })),
                    ]}
                  />
                  <Select
                    label="Área"
                    value={novaTurmaForm.area}
                    onChange={(e) => setNovaTurmaForm({ ...novaTurmaForm, area: e.target.value })}
                    options={AREAS.map((a) => ({ value: a.value, label: a.label }))}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="secondary" onClick={() => setCriandoTurma(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    icon={Save}
                    disabled={salvandoNovaTurma}
                    onClick={criarTurmaCelula}
                  >
                    {salvandoNovaTurma ? 'Criando...' : 'Criar e usar'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Buscar turma por nome..."
                      icon={Search}
                      value={buscaTurmaCelula}
                      onChange={(e) => setBuscaTurmaCelula(e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    icon={Plus}
                    onClick={() => setCriandoTurma(true)}
                  >
                    Nova turma
                  </Button>
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => escolherTurmaCelula(null)}
                    className="w-full text-left px-3 py-2 rounded-xl text-sm text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border border-transparent"
                  >
                    — Nenhuma (remover desta turma)
                  </button>
                  {opcoesCelulaEmEdicao.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                      Nenhuma turma encontrada nesse dia/horário/turno.
                    </p>
                  ) : (
                    opcoesCelulaEmEdicao.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => escolherTurmaCelula(o.value)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-900/40"
                      >
                        <span className="flex-1 min-w-0 flex items-baseline gap-1 overflow-hidden">
                          {o.professor && (
                            <span className="flex-shrink-0" translate="no">
                              {o.professor}
                            </span>
                          )}
                          <span className="truncate" translate="no">
                            {o.label}
                          </span>
                          {o.area && <span className="flex-shrink-0">· {o.area}</span>}
                          {o.ensaioNoturno && (
                            <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                              Ensaio · Noite
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
                          {o.count} inscrito{o.count === 1 ? '' : 's'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Matrículas duplicadas: nunca devia existir mais de uma matrícula
          ATIVA pro mesmo aluno no mesmo dia+horário — quando existe (bug
          antigo, ver comentário em backend/matriculas.js), esse painel deixa
          escolher manualmente qual manter; a outra é encerrada (mesmo
          soft-close de sempre, mantém o histórico). */}
      <Modal
        isOpen={duplicidadesModalAberto}
        onClose={() => setDuplicidadesModalAberto(false)}
        title="Matrículas Duplicadas"
        maxWidth="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Esses inscritos têm mais de uma matrícula ativa no mesmo dia e horário. Escolha qual
            turma manter — a outra será encerrada (o histórico continua registrado, pode reabrir
            depois em Turmas).
          </p>
          {duplicidades.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
              Nenhuma duplicidade encontrada.
            </p>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {duplicidades.map((grupo) => (
                <div
                  key={`${grupo.idaluno}-${grupo.dia_semana}-${grupo.horario}`}
                  className="p-3 rounded-xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-900/10"
                >
                  <p
                    className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-0.5"
                    translate="no"
                  >
                    {grupo.nome_aluno}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {grupo.dia_semana} · {grupo.horario}
                  </p>
                  <div className="space-y-1.5">
                    {grupo.opcoes.map((op) => (
                      <div
                        key={op.idmatricula}
                        className="flex items-center justify-between gap-3 p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800"
                      >
                        <div className="min-w-0">
                          <p
                            className="text-sm text-gray-800 dark:text-gray-200 truncate"
                            translate="no"
                          >
                            {op.nome_turma || 'Turma removida'}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {op.nome_professor && (
                              <span translate="no">{op.nome_professor} · </span>
                            )}
                            desde {op.data_inicio ? formatarDataBR(op.data_inicio) : '—'}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={resolvendoDuplicidade === op.idmatricula}
                          onClick={() => resolverDuplicidade(op.idmatricula)}
                        >
                          {resolvendoDuplicidade === op.idmatricula
                            ? 'Aplicando...'
                            : 'Manter esta'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AjusteGrade;
